import { Header } from "./componentUtils/Header";
import { Box, Card, CardContent, Grid, SvgIcon, Typography, Stack } from "@mui/material";
import { ProgressCardLarge } from "./componentUtils/ProgressCardLarge";
import { VioletCard } from "./componentUtils/VioletCard";
import { ProgressCardSmall } from "./componentUtils/ProgressCardSmall";
import { ReactComponent as MasteryIcon } from "../../../../assets/Icon-Trophy-MasteryProblem.svg";
import { ReactComponent as GuidedPracticeIcon } from "../../../../assets/Icon-Triangle-GuidedPractice.svg";
import { ReactComponent as PracticeIcon } from "../../../../assets/Icon-Basketball-PracticeProblem.svg";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  CourseMetadata,
  SummaryData,
  SummaryDataTargetProgress,
  SummaryType,
} from "@backend-types";
import axios from "../../../../services/axios";
import { InsightsProgressFilterContext } from "../../../../context/InsightsProgressFilterContext";
import {
  InsightsView,
  InsightsFilterCtx,
  UniversalModalCtx,
  ClassExtended,
  DbUser,
} from "../../../../interfaces";
import { v2theme } from "../../../../styles/Theme";
import { makeStyles } from "@mui/styles";
import { ParagraphTooltip } from "../../utils/CustomTooltip";
import { FilterObj } from "../../../../types";
import { useDidMountEffect } from "../../../../hooks/useDidMount";
import { UniversalModalContext } from "../../../../context/UniversalModalContext";
import { DownloadableReport } from "../../../../hooks/useXLSXGenerator";
import { sanitizeData } from "../../../../Helpers";

const useStyle = makeStyles((theme) => ({
  insufficientData: {
    textAlign: "center",
    color: "#787676",
    maxWidth: 378,
  },
  targetPerfCard: {
    borderRadius: 16,
    border: 0,
    padding: 32,
    height: 515,
  },
  problemCardContainer: {
    display: "flex",
    flexDirection: "row",
    gap: "2rem",
    marginTop: "32px",
    [theme.breakpoints.down("xl")]: {
      gap: "1rem",
    },
  },
  hintsAndErrorsCard: {
    width: "100%",
    height: "100%",
    border: "0px",
    borderRadius: "16px",
    padding: "32px",
    [theme.breakpoints.down("lg")]: {
      padding: "1rem",
    },
  },
}));

const SingleTargetSummaryItem = (props: { title: string; value: number }) => {
  return (
    <Box
      sx={{ display: "flex", flexDirection: "row", gap: "1rem", alignItems: "center" }}>
      <Box
        sx={{
          background: "#CECECE 0% 0% no-repeat padding-box",
          borderRadius: "8px",
          width: "48px",
          height: "48px",
          fontSize: "24px",
          display: "inline-block",
          lineHeight: "48px",
          textAlign: "center",
          verticalAlign: "bottom",
          color: "#fff",
        }}>
        <i className="fa-regular fa-folder"></i>
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
        <Typography
          component={"span"}
          variant={"body1"}
          sx={{
            font: "normal normal normal 14px/25px Roboto",
            textAlign: "left",
            whiteSpace: "nowrap",
            maxWidth: "440px",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}>
          {props.title}
        </Typography>
        <Typography
          component={"span"}
          variant={"body1"}
          sx={{ font: "normal normal bold 18px/24px proxima-soft", textAlign: "left" }}>
          {props.value}%
        </Typography>
      </Box>
    </Box>
  );
};

interface SummaryProps {
  classId: number;
  defaultModuleIds: string[];
  studentId?: string | null;
  view: InsightsView;
  classDetails: ClassExtended;
  studentDetails?: DbUser | null;
  courseMetadata?: CourseMetadata;
}

export interface SummaryRequest {
  type: SummaryType;
  startDate: string;
  endDate: string;
  classes?: number[];
  students?: Entity[] | null;
  courseFilter?: CourseFilter;
  v2Feedback: boolean;
}
export interface Entity {
  id: number | string | undefined;
}
export interface CourseFilter {
  moduleIds?: string[] | null;
  targetIds?: string[] | null;
}

const getHintAndErrorVioletCardData = (props: {
  summaryData: SummaryData;
  studentId: string | null | undefined;
}) => {
  const { summaryData, studentId } = props;
  if (studentId) {
    return (
      <Grid container spacing={4} sx={{ height: "100%" }}>
        <Grid item xs={6}>
          <VioletCard>
            <CardContent>
              <Box sx={{ display: "flex", flexDirection: "column", padding: "20px 0px" }}>
                <Typography
                  component={"span"}
                  sx={{
                    font: "normal normal normal 14px/17px Roboto",
                    textAlign: "center",
                    color: "#FFFFFF",
                  }}
                  variant="body1">
                  Avg. # of errors made
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "center",
                    alignItems: "flex-end",
                    marginTop: "24px",
                  }}>
                  <Typography
                    component={"span"}
                    sx={{
                      font: "normal normal bold 30px/24px Roboto",
                      textAlign: "center",
                      color: "#ffffff",
                    }}>
                    {summaryData.feedbackErrorAvg || 0}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </VioletCard>
        </Grid>
        <Grid item xs={6}>
          <VioletCard>
            <CardContent>
              <Box sx={{ display: "flex", flexDirection: "column", padding: "20px 0px" }}>
                <Typography
                  component={"span"}
                  sx={{
                    font: "normal normal normal 14px/17px Roboto",
                    textAlign: "center",
                    color: "#FFFFFF",
                  }}
                  variant="body1">
                  Avg. # of hints used
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "center",
                    alignItems: "center",
                    marginTop: "24px",
                  }}>
                  <Typography
                    component={"span"}
                    sx={{
                      font: "normal normal bold 30px/24px Roboto",
                      textAlign: "center",
                      color: "#ffffff",
                    }}>
                    {summaryData.feedbackHintAvg || 0}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </VioletCard>
        </Grid>
      </Grid>
    );
  } else {
    return (
      <Grid container spacing={4} sx={{ height: "100%" }}>
        <Grid item xs={4}>
          <VioletCard>
            <CardContent>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography
                  component={"span"}
                  sx={{
                    font: "normal normal normal 14px/17px Roboto",
                    textAlign: "center",
                    color: "#FFFFFF",
                  }}
                  variant="body1">
                  Avg. # of hints viewed
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "center",
                    alignItems: "center",
                    marginTop: "24px",
                    position: "relative",
                  }}>
                  <Typography
                    component={"span"}
                    sx={{
                      font: "normal normal bold 30px/24px Roboto",
                      textAlign: "center",
                      color: "#ffffff",
                    }}>
                    {summaryData.feedbackHintAvg || 0}
                  </Typography>
                  <ParagraphTooltip
                    arrow
                    title={
                      "This is an average amount of hints viewed per student, per problem. It’s first calculated by total hints viewed / total problems attempted."
                    }>
                    <Typography
                      component={"span"}
                      sx={{
                        position: "absolute",
                        right: -16,
                        font: "normal normal bold 16px/16px Roboto",
                        color: "#ffffff",
                      }}>
                      <i className="fa-solid fa-circle-info"></i>
                    </Typography>
                  </ParagraphTooltip>
                </Box>
              </Box>
            </CardContent>
          </VioletCard>
        </Grid>
        <Grid item xs={8}>
          <VioletCard>
            <CardContent>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography
                  component={"span"}
                  sx={{
                    font: "normal normal normal 14px/17px Roboto",
                    textAlign: "center",
                    color: "#FFFFFF",
                  }}
                  variant="body1">
                  Top students with hints and errors
                </Typography>
                <Typography
                  component={"span"}
                  sx={{
                    marginTop: "16px",
                    font: "normal normal bold 20px/28px Roboto",
                    textAlign: "center",
                    color: "#ffffff",
                  }}>
                  {summaryData.topFeedback?.slice(0, 3).map((student, index) => {
                    return <Box key={`topfeedback-${index}`}>{student}</Box>;
                  })}
                </Typography>
              </Box>
            </CardContent>
          </VioletCard>
        </Grid>
      </Grid>
    );
  }
};

export const SummaryView = (props: SummaryProps) => {
  const classes = useStyle();
  const universalModalCtx = useContext(UniversalModalContext) as UniversalModalCtx;
  const { classId, studentId, view, classDetails, studentDetails, courseMetadata } =
    props;
  const [summaryMap, setSummaryMap] = useState<Map<SummaryType, SummaryData>>(
    new Map<SummaryType, SummaryData>()
  );
  const [summaryData, setSummaryData] = useState<SummaryData>({} as SummaryData);
  const [summaryDateFilter, setSummaryDateFilter] = useState<FilterObj>({} as FilterObj);

  const insightsProgressFilterCtx = useContext(
    InsightsProgressFilterContext
  ) as InsightsFilterCtx;
  const insightsProgressFilterCtxRef = useRef(insightsProgressFilterCtx);
  insightsProgressFilterCtxRef.current = insightsProgressFilterCtx;

  const getLoaderMessage = (insightsView: InsightsView) => {
    if (insightsView === "class") {
      return "Loading class...";
    } else {
      return "Loading student insights";
    }
  };

  const updateSummaryMap = (key: SummaryType, value: SummaryData) => {
    setSummaryMap((prevMap) => {
      const updatedMap = new Map(prevMap);
      updatedMap.set(key, value);
      return updatedMap;
    });
  };

  useEffect(() => {
    const summaryProgress = summaryMap.get("summary-progress");
    if (!summaryProgress) return;
    const summaryFeedback = summaryMap.get("summary-feedback");
    const summaryTopFeedback = summaryMap.get("summary-top-feedback");
    const summaryTargetProgress = summaryMap.get("summary-target-progress");
    const newSummary: SummaryData = {
      id: summaryProgress.id,
      percentage: summaryProgress.percentage,
      donutPercentage: summaryProgress.donutPercentage,
      GPCorrect: summaryProgress.GPCorrect,
      GPAttempted: summaryProgress.GPAttempted,
      GPTotal: summaryProgress.GPTotal,
      PPCorrect: summaryProgress.PPCorrect,
      PPAttempted: summaryProgress.PPAttempted,
      PPTotal: summaryProgress.PPTotal,
      MPCorrect: summaryProgress.MPCorrect,
      MPAttempted: summaryProgress.MPAttempted,
      MPTotal: summaryProgress.MPTotal,
      feedbackError: summaryFeedback?.feedbackError ?? 0,
      feedbackHint: summaryFeedback?.feedbackHint ?? 0,
      feedbackErrorAvg: summaryFeedback?.feedbackErrorAvg ?? 0,
      feedbackHintAvg: summaryFeedback?.feedbackHintAvg ?? 0,
      topFeedback: summaryTopFeedback?.topFeedback ?? [],
      targetProgress: summaryTargetProgress?.targetProgress ?? [],
    };
    setSummaryData(newSummary);
  }, [summaryMap]);

  useDidMountEffect(() => {
    if (!studentId && !classId) return;
    let courseFilter = insightsProgressFilterCtx.selectedCourseFilter ?? {
      moduleIds: props.defaultModuleIds,
    };
    if (!courseFilter) return;
    const getSummaryData = async (endpoint: string, requestData: SummaryRequest) => {
      try {
        universalModalCtx.setLoaderDialogText(getLoaderMessage(view));
        universalModalCtx.setLoaderDialogOpen(true);
        let response = await axios.post(`/insights/${endpoint}`, requestData);
        let data = response.data[0];
        data.targetProgress
          .sort((a: SummaryDataTargetProgress, b: SummaryDataTargetProgress) => {
            return b.percentage - a.percentage;
          })
          .slice(0, 10);
        // setSummaryData(data);
        updateSummaryMap(requestData.type, data);
        universalModalCtx.setLoaderDialogOpen(false);
      } catch (e) {
        console.log(e);
        universalModalCtx.setLoaderDialogOpen(false);
      }
    };
    const getClassSummary = async (type: SummaryType) => {
      let data = {
        type: type,
        startDate: summaryDateFilter.startDate,
        endDate: summaryDateFilter.endDate,
        classIds: [Number(classId)],
        courseFilter: courseFilter,
        v2Feedback: courseMetadata?.insights_page?.use_user_events_v2 ?? false,
      } as SummaryRequest;
      // if (Object.keys(targetDateFilter).length > 0) {
      //   data = {
      //     ...data,
      //     ...{
      //       targetProgressFilter: {
      //         startDate: targetDateFilter.startDate,
      //         endDate: targetDateFilter.endDate,
      //       },
      //     },
      //   };
      // }
      if (insightsProgressFilterCtxRef.current.filters.length > 0) {
        data = {
          ...data,
          ...{
            courseFilter: courseFilter,
          },
        } as SummaryRequest;
      }
      await getSummaryData("class-summary", data);
    };
    const getStudentSummary = async (type: SummaryType) => {
      let data = {
        type: type,
        startDate: summaryDateFilter.startDate,
        endDate: summaryDateFilter.endDate,
        classIds: [classId],
        students: [
          {
            id: studentId,
          },
        ],
        courseFilter: courseFilter,
        v2Feedback: courseMetadata?.insights_page?.use_user_events_v2 ?? false,
      } as SummaryRequest;
      // if (Object.keys(targetDateFilter).length > 0) {
      //   data = {
      //     ...data,
      //     ...{
      //       targetProgressFilter: {
      //         startDate: targetDateFilter.startDate,
      //         endDate: targetDateFilter.endDate,
      //       },
      //     },
      //   };
      // }
      if (insightsProgressFilterCtxRef.current.filters.length > 0) {
        data = {
          ...data,
          ...{
            courseFilter: courseFilter,
          },
        } as SummaryRequest;
      }
      await getSummaryData("student-summary", data);
    };
    if (!studentId) {
      getClassSummary("summary-progress").then(() =>
        getClassSummary("summary-feedback").then(() =>
          getClassSummary("summary-top-feedback").then(() =>
            getClassSummary("summary-target-progress")
          )
        )
      );
    } else {
      getStudentSummary("summary-progress").then(() =>
        getStudentSummary("summary-feedback").then(() =>
          getStudentSummary("summary-target-progress")
        )
      );
    }
  }, [
    classId,
    studentId,
    summaryDateFilter,
    insightsProgressFilterCtx.selectedCourseFilter,
    courseMetadata?.insights_page?.use_user_events_v2,
  ]);

  const correctSubmissions = Math.round(
    summaryData.MPCorrect + summaryData.GPCorrect + summaryData.PPCorrect
  );
  const incorrectSubmissions = Math.round(
    summaryData.MPAttempted + summaryData.GPAttempted + summaryData.PPAttempted
  );
  const totalSubmissionsVal = Math.round(correctSubmissions + incorrectSubmissions);

  const totalProblems = Math.round(
    summaryData.MPTotal + summaryData.GPTotal + summaryData.PPTotal
  );

  const highestChunk = useMemo<SummaryDataTargetProgress[]>(() => {
    return (
      summaryData.targetProgress
        ?.slice(
          0,
          summaryData.targetProgress.length / 2 +
            (summaryData.targetProgress.length % 2 === 0 ? 0 : 1)
        )
        .slice(0, 5) ?? []
    );
  }, [summaryData]);

  const lowestChunk = useMemo<SummaryDataTargetProgress[]>(() => {
    return (
      summaryData.targetProgress
        ?.slice(
          summaryData.targetProgress.length / 2 +
            (summaryData.targetProgress.length % 2 === 0 ? 0 : 1),
          summaryData.targetProgress.length
        )
        .reverse()
        .slice(0, 5) ?? []
    );
  }, [summaryData]);

  const getInsufficientDataMessage = (message: string, length: number) => {
    let paddingTop = 0;
    switch (length) {
      case 1:
        paddingTop = 80;
        break;
      case 2:
        paddingTop = 6;
        break;
    }

    return (
      <Stack
        flexDirection="row"
        justifyContent="center"
        alignItems={length === 0 ? "center" : undefined}
        sx={{ height: "100%", width: "100%" }}>
        <Typography
          variant="body2"
          className={classes.insufficientData}
          sx={{ paddingTop: `${paddingTop}px` }}>
          {message}
        </Typography>
      </Stack>
    );
  };

  const classSummaryReport = useMemo<DownloadableReport>(() => {
    const enableFeedback = !courseMetadata?.insights_page?.disable_feedback_data;
    const enableGPP = !courseMetadata?.insights_page?.disable_gpp_card;
    const modifiedData = {
      ...(enableFeedback && {
        "Avg. # of errors made": summaryData.feedbackError || 0,
        "Avg. # of hints used": summaryData.feedbackHint || 0,
        "Avg. # of hints viewed": summaryData.feedbackHintAvg || 0,
        "Top students with hints and errors": summaryData.topFeedback
          ?.slice(0, 3)
          .map((student, index) => {
            return student;
          })
          .join(", "),
      }),

      "% Correct of all problems attempted": Math.round(
        summaryData.donutPercentage * 100
      ),
      "Completed problems": totalSubmissionsVal,
      "Total problems": totalProblems,

      "% Correct of mastery problems attempted": Math.round(
        (summaryData.MPCorrect / (summaryData.MPCorrect + summaryData.MPAttempted)) * 100
      ),
      "% Overall mastery problem progress": Math.round(
        ((summaryData.MPCorrect + summaryData.MPAttempted) / summaryData.MPTotal) * 100
      ),
      "Completed mastery problems": Math.ceil(
        summaryData.MPCorrect + summaryData.MPAttempted
      ),
      "Total mastery problems": Math.round(summaryData.MPTotal),

      ...(enableGPP && {
        "% Correct of guided practice problems attempted": Math.round(
          (summaryData.GPCorrect / (summaryData.GPCorrect + summaryData.GPAttempted)) *
            100
        ),
        "% Overall guided practice problem progress": Math.round(
          ((summaryData.GPCorrect + summaryData.GPAttempted) / summaryData.GPTotal) * 100
        ),
        "Completed guided practice problems": Math.ceil(
          summaryData.GPCorrect + summaryData.GPAttempted
        ),
        "Total guided practice problems": Math.round(summaryData.GPTotal),
      }),

      "% Correct of practice problems attempted": Math.round(
        (summaryData.PPCorrect / (summaryData.PPCorrect + summaryData.PPAttempted)) * 100
      ),
      "% Overall practice problem progress": Math.round(
        ((summaryData.PPCorrect + summaryData.PPAttempted) / summaryData.PPTotal) * 100
      ),
      "Completed practice problems": Math.ceil(
        summaryData.PPCorrect + summaryData.PPAttempted
      ),
      "Total practice problems": Math.round(summaryData.PPTotal),
    };

    return {
      fileName:
        view === "class"
          ? `${classDetails.subject} ${classDetails.period_block} - Overall Progress`
          : `${classDetails.subject} ${classDetails.period_block} - ${studentDetails?.full_name} - Overall Progress`,
      sheets: [
        {
          name: "Overall Progress",
          header: ["heading1"],
          data: [sanitizeData(modifiedData)],
        },
      ],
    };
  }, [
    classDetails.subject,
    classDetails.period_block,
    view,
    studentDetails?.full_name,
    summaryData,
    totalProblems,
    totalSubmissionsVal,
    courseMetadata?.insights_page?.disable_feedback_data,
    courseMetadata?.insights_page?.disable_gpp_card,
  ]);

  return (
    <>
      <Header
        title="Overall progress"
        onDateFilterChange={(filterObj) => {
          setSummaryDateFilter(filterObj);
        }}
        downloadableReport={classSummaryReport}
      />
      <Grid container columnSpacing={7} sx={{ marginTop: "24px" }}>
        <Grid item xs={6}>
          <ProgressCardLarge
            body={
              studentId
                ? "This chart demonstrates student progress through all problems available in the Digit platform."
                : "This chart demonstrates the average student progress through all problems available in the Digit platform."
            }
            donutProps={{
              label: "Correct of total attempted",
              value: Math.round(summaryData.donutPercentage * 100),
              "aria-label": "all problems correct",
            }}
            progressBarProps={{
              name: "Overall problems",
              value: Math.round((totalSubmissionsVal / totalProblems) * 100),
              fullWidth: true,
            }}
            legendProps={{
              legends: [
                {
                  hex: v2theme.palette.primary.main,
                  text: `${totalSubmissionsVal} Completed`,
                },
                {
                  hex: "#BFBFBF",
                  text: `${totalProblems} Total`,
                },
              ],
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <Card className={classes.hintsAndErrorsCard}>
            <CardContent
              sx={{
                display: "flex",
                flexDirection: "column",
                padding: "0 !important",
                height: "100%",
              }}>
              <Typography
                component={"span"}
                sx={{
                  font: "normal normal 600 20px/24px proxima-soft",
                  marginBottom: "24px",
                }}
                variant="body1">
                <i
                  style={{ marginRight: "12.5px" }}
                  className="fa-regular fa-lightbulb"></i>
                Hints and errors
              </Typography>
              {courseMetadata?.insights_page?.disable_feedback_data ? (
                <Typography variant="body1">
                  Hints and errors data coming soon!
                </Typography>
              ) : (
                getHintAndErrorVioletCardData({ studentId, summaryData })
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      {/*Problem wise charts*/}
      <Box
        className={classes.problemCardContainer}
        sx={{
          gap: courseMetadata?.insights_page?.disable_gpp_card
            ? "60px !important"
            : undefined,
        }}>
        <ProgressCardSmall
          title="Mastery problems"
          icon={
            <SvgIcon style={{ alignSelf: "center" }} viewBox="0 0 28 28">
              <MasteryIcon />
            </SvgIcon>
          }
          donutProps={{
            value: Math.round(
              (summaryData.MPCorrect /
                (summaryData.MPCorrect + summaryData.MPAttempted)) *
                100
            ),
            label: "Correct",
            small: true,
            "aria-label": `Circular pie chart. Student has answered 10% of the guided practice problems correct.`,
          }}
          progressBarProps={{
            name: "Mastery problems",
            value: Math.round(
              ((summaryData.MPCorrect + summaryData.MPAttempted) / summaryData.MPTotal) *
                100
            ),
            fullWidth: true,
          }}
          legendProps={{
            legends: [
              {
                hex: v2theme.palette.primary.main,
                text: `${Math.ceil(
                  summaryData.MPCorrect + summaryData.MPAttempted
                )} Completed`,
              },
              {
                hex: "#E8E8E8",
                text: `${Math.round(summaryData.MPTotal)} Total`,
              },
            ],
          }}
        />
        {!courseMetadata?.insights_page?.disable_gpp_card && (
          <ProgressCardSmall
            title="Guided practice problems"
            icon={
              <SvgIcon style={{ alignSelf: "center" }} viewBox="0 0 28 28">
                <GuidedPracticeIcon />
              </SvgIcon>
            }
            donutProps={{
              value: Math.round(
                (summaryData.GPCorrect /
                  (summaryData.GPCorrect + summaryData.GPAttempted)) *
                  100
              ),
              label: "Correct",
              small: true,
              "aria-label": `Circular pie chart. Student has answered 10% of the guided practice problems correct.`,
            }}
            progressBarProps={{
              name: "Guided practice problems",
              value: Math.round(
                ((summaryData.GPCorrect + summaryData.GPAttempted) /
                  summaryData.GPTotal) *
                  100
              ),
              fullWidth: true,
            }}
            legendProps={{
              legends: [
                {
                  hex: v2theme.palette.primary.main,
                  text: `${Math.ceil(
                    summaryData.GPCorrect + summaryData.GPAttempted
                  )} Completed`,
                },
                {
                  hex: "#E8E8E8",
                  text: `${Math.round(summaryData.GPTotal)} Total`,
                },
              ],
            }}
          />
        )}
        <ProgressCardSmall
          title="Practice problems"
          icon={
            <SvgIcon style={{ alignSelf: "center" }} viewBox="0 0 28 28">
              <PracticeIcon />
            </SvgIcon>
          }
          donutProps={{
            value: Math.round(
              (summaryData.PPCorrect /
                (summaryData.PPCorrect + summaryData.PPAttempted)) *
                100
            ),
            label: "Correct",
            small: true,
            "aria-label": `Circular pie chart. Student has answered 10% of the guided practice problems correct.`,
          }}
          progressBarProps={{
            name: "Guided practice problems",
            value: Math.round(
              ((summaryData.PPCorrect + summaryData.PPAttempted) / summaryData.PPTotal) *
                100
            ),
            fullWidth: true,
          }}
          legendProps={{
            legends: [
              {
                hex: v2theme.palette.primary.main,
                text: `${Math.ceil(
                  summaryData.PPCorrect + summaryData.PPAttempted
                )} Completed`,
              },
              {
                hex: "#E8E8E8",
                text: `${Math.round(summaryData.PPTotal)} Total`,
              },
            ],
          }}
        />
      </Box>
      {/*Learning Target Summary*/}
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          marginTop: "32px",
          alignItems: "center",
        }}>
        <Typography component={"span"} variant={"h3"} sx={{ marginRight: "auto" }}>
          Learning target summary
        </Typography>
        {/*<DateFilterDropdown*/}
        {/*  availableFilters={AVAILABLE_DATE_FILTERS}*/}
        {/*  onFilterChange={(filterObj) => {*/}
        {/*    setTargetDateFilter(filterObj);*/}
        {/*  }}*/}
        {/*/>*/}
      </Box>
      <Grid container spacing={7.5} sx={{ marginTop: "32px" }}>
        <Grid sx={{ paddingTop: "0px !important" }} item xs={12} md={6}>
          <Card className={classes.targetPerfCard}>
            <CardContent sx={{ height: "100%" }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "24px",
                  position: "relative",
                  height: "100%",
                }}>
                <Typography
                  component={"span"}
                  variant={"h5"}
                  sx={{ fontWeight: "600", letterSpacing: "0px", color: "#484848" }}>
                  Highest performing targets
                  <ParagraphTooltip
                    title={
                      "Highest performing targets are calculated by (Correct Practice Problems + Attempted Practice Problems + Correct Mastery Problems) / (Total Practice Problems + Total Mastery Problems) for the specific learning target."
                    }>
                    <i
                      style={{ marginLeft: "12px" }}
                      className="fa-solid fa-circle-info"></i>
                  </ParagraphTooltip>
                </Typography>
                {highestChunk.length > 0
                  ? highestChunk.map((target, index) => {
                      let percentage = Math.round(target.percentage * 100);
                      return (
                        <SingleTargetSummaryItem
                          key={`${index}-${target.name}-high`}
                          title={target.name}
                          value={percentage}
                        />
                      );
                    })
                  : getInsufficientDataMessage(
                      "No sufficient data to display. Check back as student(s) submit work for multiple learning targets.",
                      highestChunk.length
                    )}

                {highestChunk.length < 5 &&
                  highestChunk.length > 0 &&
                  getInsufficientDataMessage(
                    "More targets will load here as student(s) submit work for multiple learning targets.",
                    highestChunk.length
                  )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid sx={{ paddingTop: "0px !important" }} item xs={12} md={6}>
          <Card className={classes.targetPerfCard}>
            <CardContent sx={{ height: "100%" }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "24px",
                  position: "relative",
                  height: "100%",
                }}>
                <Typography
                  component={"span"}
                  variant={"h5"}
                  sx={{ fontWeight: "600", letterSpacing: "0px", color: "#484848" }}>
                  Lowest performing Targets
                  <ParagraphTooltip
                    title={
                      "Lowest performing targets are calculated by (Correct Practice Problems + Attempted Practice Problems + Correct Mastery Problems) / (Total Practice Problems + Total Mastery Problems) for the specific learning target."
                    }>
                    <i
                      style={{ marginLeft: "12px" }}
                      className="fa-solid fa-circle-info"></i>
                  </ParagraphTooltip>
                </Typography>
                {lowestChunk.length > 0
                  ? lowestChunk.map((target, index) => {
                      let percentage = Math.round(target.percentage * 100);
                      return (
                        <SingleTargetSummaryItem
                          key={`${index}-${target.name}-low`}
                          title={target.name}
                          value={percentage}
                        />
                      );
                    })
                  : getInsufficientDataMessage(
                      "No sufficient data to display. Check back as student(s) submit work for multiple learning targets.",
                      lowestChunk.length
                    )}

                {lowestChunk.length < 5 &&
                  lowestChunk.length > 0 &&
                  getInsufficientDataMessage(
                    "More targets will load here as student(s) submit work for multiple learning targets.",
                    lowestChunk.length
                  )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </>
  );
};
